import { AddOnsProduct, AddOnsProductResponse } from "../model";
import { getLocalApi } from "./localApi";
import apiConfig from "app/api/apiConfig.json";
import { ApiErrors } from "./ApiErrors";

export interface SaveAddOnProductResult {
  ok: boolean;
  message?: string;
  errors?: string[];
  productId?: number;
}

const parseResponse = async (response: Response | undefined) => {
  if (!response) {
    return { message: undefined, productId: undefined };
  }

  try {
    const data = await response.clone().json();
    if (typeof data === 'string') {
      const numeric = Number(data);
      return {
        message: data,
        productId: Number.isNaN(numeric) ? undefined : numeric,
      };
    }

    if (data && typeof data === 'object') {
      const message =
        typeof data.message === 'string' ? data.message : undefined;
      const productId =
        typeof data.productId === 'number'
          ? data.productId
          : typeof data.id === 'number'
            ? data.id
            : undefined;
      return { message, productId };
    }
  } catch {
    // fall back to text
  }

  try {
    const text = await response.text();
    const numeric = Number(text);
    return {
      message: text,
      productId: Number.isNaN(numeric) ? undefined : numeric,
    };
  } catch {
    return { message: undefined, productId: undefined };
  }
};

export async function saveAddOnProduct(
  product: AddOnsProduct,
  divisionId: number
): Promise<SaveAddOnProductResult> {

  const localApi = await getLocalApi();

  const payload: AddOnsProductResponse = {
    productId: Number(product.id ?? 0),
    productName: product.name,
    unitTypeId: Number(product.unitTypeId),
    productCategoryId: product.productCategoryId !== undefined ? Number(product.productCategoryId) : undefined,
    accountCategoryId: product.accountCategoryId !== undefined ? Number(product.accountCategoryId) : undefined,
    typeId: Number(product.typeId ?? 0),
    minimumAge: product.minimumAge ?? null,
    active: product.active !== false,
    numberOfOneToOneLessonsPerWeek: Number(product.numberOfOneToOneLessonsPerWeek ?? 0)
  };

  const method = "PUT";

  const result = await localApi.call(
    apiConfig.addons_save.replace("{divisionId}", divisionId.toString()),
    method,
    JSON.stringify(payload)
  );

  if (!result) {
    return { ok: false, message: 'No response from API', errors: ['No response from API'] };
  }

  const { message, productId } = await parseResponse(result);
  
  let rawText = "";
  try {
    rawText = await result.clone().text();
  } catch {
    rawText = "";
  }

  let parsed: any = null;
  if (rawText) {
    try {
      parsed = JSON.parse(rawText);
    } catch {
      parsed = rawText;
    }
  }

  const errors = ApiErrors.extractAllErrors(parsed, result, "Failed to save add-on product");
  const finalMessage = result.ok
    ? (message || "Add-on product saved")
    : ApiErrors.formatErrors(errors);

  return {
    ok: result.ok,
    message: finalMessage,
    errors: result.ok ? undefined : errors,
    productId
  };
}
